/**
 * The time half of the two scheduling pickers.
 *
 * **monday stores `hour` in UTC** and keeps the day carry in `utcDaysDiff` (measured twice on
 * the sandbox: 7:15 AM Europe/Madrid → `hour: "5.25"`, `discovery/automations/
 * sandbox-experiments.md` E3/E4). The picker shows a local wall-clock time in 15-minute steps
 * (E16), so these two functions are the only place the conversion happens.
 */

export const TIME_STEP_MINUTES = 15;

export interface LocalTime {
  hours: number;
  minutes: number;
}

/** The browser's own zone, which is what monday writes into `timezone`. */
export function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Minutes east of UTC for `timezone` at `at` (Europe/Madrid in summer → 120). */
export function utcOffsetMinutes(timezone: string, at: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at);
    const get = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? '0');
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60_000);
  } catch {
    return 0;
  }
}

/** Local wall clock → monday's `{hour, utcDaysDiff}`. */
export function toUtcHour(time: LocalTime, timezone: string): { hour: number; utcDaysDiff: number } {
  const offset = utcOffsetMinutes(timezone);
  let minutes = time.hours * 60 + time.minutes - offset;
  let utcDaysDiff = 0;
  if (minutes < 0) {
    minutes += 1440;
    utcDaysDiff = -1;
  } else if (minutes >= 1440) {
    minutes -= 1440;
    utcDaysDiff = 1;
  }
  return { hour: Math.round((minutes / 60) * 100) / 100, utcDaysDiff };
}

/** monday's `{hour, utcDaysDiff}` → the local wall clock the picker shows. */
export function toLocalTime(hour: number, utcDaysDiff: number, timezone: string): LocalTime {
  const offset = utcOffsetMinutes(timezone);
  let minutes = Math.round(hour * 60) + offset + utcDaysDiff * 1440;
  minutes = ((minutes % 1440) + 1440) % 1440;
  return { hours: Math.floor(minutes / 60), minutes: minutes % 60 };
}

/** `{hours: 7, minutes: 15}` → `7:15 AM`, the format the capture shows. */
export function formatTime(time: LocalTime): string {
  const suffix = time.hours < 12 ? 'AM' : 'PM';
  const hour12 = time.hours % 12 === 0 ? 12 : time.hours % 12;
  return `${String(hour12)}:${String(time.minutes).padStart(2, '0')} ${suffix}`;
}

/** The 96 quarter-hour options of the searchable dropdown (`32-time-dropdown.png`). */
export function timeOptions(): Array<{ value: string; text: string; time: LocalTime }> {
  const options: Array<{ value: string; text: string; time: LocalTime }> = [];
  for (let minutes = 0; minutes < 1440; minutes += TIME_STEP_MINUTES) {
    const time = { hours: Math.floor(minutes / 60), minutes: minutes % 60 };
    options.push({ value: `${String(time.hours)}:${String(time.minutes).padStart(2, '0')}`, text: formatTime(time), time });
  }
  return options;
}

export function parseTimeValue(value: string): LocalTime {
  const [hours = '0', minutes = '0'] = value.split(':');
  return { hours: Number(hours), minutes: Number(minutes) };
}
